import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Csel,
  Destructive,
  Icon,
  Reason,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Switch,
  useToast,
} from '@efira/ui';

import {
  useAddLibrarySound,
  useAddSoundByUrl,
  usePatchSound,
  useRemoveSound,
  useReorderSounds,
  useSoundLibrary,
  useSounds,
  useUploadSound,
} from '../../api/queries';
import type { SoundRow } from '../../api/queries';
import { useGifts } from '../../api/queries';

/**
 * Библиотека звуков - раздел настроек.
 *
 * ЗДЕСЬ ПРОСЛУШИВАНИЕ ЛОКАЛЬНОЕ. Кнопка номера пада проигрывает звук в этом
 * браузере и в эфир не идёт: человек выбирает файл, а не даёт его зрителям.
 * В эфир звук уходит только нажатием пада в правой колонке, и обе кнопки
 * подписаны так, чтобы разницу не приходилось угадывать.
 *
 * ТРИГГЕР СЧИТАЕТ ОБЩИЙ ВЫЧИСЛИТЕЛЬ ПРИВЯЗОК. Шесть значений - вручную,
 * ключевое слово, подарок, подписка, репост, вход - ложатся на наш словарь
 * событий; второй модели триггеров в продукте нет.
 */

const TRIGGERS = [
  { value: 'manual', label: 'Вручную' },
  { value: 'keyword', label: 'Ключевое слово' },
  { value: 'gift', label: 'Подарок' },
  { value: 'follow', label: 'Подписка' },
  { value: 'share', label: 'Репост' },
  { value: 'join', label: 'Вход в эфир' },
];

/*
 * ЦВЕТА ПАДОВ - СОДЕРЖИМОЕ, А НЕ ОФОРМЛЕНИЕ.
 *
 * Это палитра, из которой человек выбирает метку своему звуку: значение
 * настройки, которое хранится в его аккаунте и уезжает на плитку. Токенами
 * темы её задать нельзя - она обязана оставаться одинаковой в обеих темах,
 * иначе выбранный «синий» на светлой теме станет другим на тёмной.
 */
// eslint-disable-next-line no-restricted-syntax -- палитра выбора пользователя: значение настройки, а не цвет интерфейса
const COLORS = ['#12b58c', '#e0842c', '#3b82f6', '#a855f7', '#e03434', '#64748b'];

export function SoundLibrary() {
  const sounds = useSounds();
  const reorder = useReorderSounds();
  const [browsing, setBrowsing] = useState(false);

  const data = sounds.data;
  const rows = data?.sounds ?? [];
  const limits = data?.limits;

  if (browsing) {
    return (
      <SoundBrowser
        onBack={() => {
          setBrowsing(false);
        }}
      />
    );
  }

  return (
    <div data-djpad="root" className="flex flex-col gap-3">
      {/* Заголовок В ТЕЛЕ раздела: без него название живёт только подсветкой
          пункта меню, и уровней иерархии остаётся два вместо трёх. */}
      <h2 className="text-foreground text-xl font-semibold">Звуки и пады</h2>

      <p data-djpad="intro" className="text-foreground/80 text-sm">
        Звуки в этом списке становятся падами в правой колонке — по порядку, перетаскиванием или
        стрелками. Нажатие пада отправляет звук <strong className="text-foreground">в эфир</strong>;
        кнопка с номером здесь проигрывает его только вам. Свои файлы — MP3, OGG или WAV, до{' '}
        {Math.round((limits?.maxBytes ?? 5_242_880) / 1024 / 1024)} МБ каждый.
      </p>

      <div data-control-row="djpad-head" className="@container flex items-center gap-2">
        <span data-djpad="count" className="text-foreground min-w-0 flex-1 truncate text-sm">
          Загружено {rows.length} из {limits?.perAccount ?? 50} своих звуков
        </span>

        <AddByFile />
        <AddByUrl />

        {/* Действие названо глаголом и ведёт на экран с тем же именем. */}
        <Button
          variant="secondary"
          size="s"
          data-djpad="browse"
          onClick={() => {
            setBrowsing(true);
          }}
        >
          Выбрать из набора · {data?.library ?? 0}
        </Button>
      </div>

      {rows.length === 0 ? (
        <p data-djpad="empty" className="text-foreground/80 text-sm">
          Пока пусто. Возьмите готовые звуки из нашего набора — это быстрее всего, — или загрузите
          свой файл.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((sound, index) => (
            <SoundRowView
              key={sound.id}
              sound={sound}
              index={index}
              total={rows.length}
              onDrop={(from, to) => {
                const ids = rows.map((item) => item.id);
                const [moved] = ids.splice(from, 1);

                if (moved !== undefined) {
                  ids.splice(to, 0, moved);
                  reorder.mutate(ids);
                }
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Загрузка файла: тип проверяется сервером по содержимому. */
function AddByFile() {
  const uploadSound = useUploadSound();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- скрытый файловый
          ввод: органа управления на экране нет, его открывает кнопка рядом.
          У пресета компонента для файла нет. */}
      <input
        ref={input}
        type="file"
        data-djpad="upload-input"
        accept="audio/mpeg,audio/ogg,audio/wav,.mp3,.ogg,.wav"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';

          if (file === undefined) {
            return;
          }

          uploadSound.mutate(file, {
            onSuccess: () => {
              toast.show({ tone: 'success', title: 'Звук добавлен' });
            },
          });
        }}
      />

      <Button
        variant="secondary"
        size="s"
        className="gap-2"
        data-djpad="upload"
        disabled={uploadSound.isPending}
        onClick={() => {
          input.current?.click();
        }}
      >
        <Icon name="upload" size="sm" />
        Загрузить
      </Button>
    </>
  );
}

/**
 * Добавление по ссылке: отдельная встроенная панель, поле и кнопка.
 *
 * Файл скачиваем МЫ и кладём к себе: оверлей не ходит на чужие адреса в
 * эфире - чужой сервер отвечает медленно или не отвечает ровно тогда, когда
 * идёт стрим.
 */
function AddByUrl() {
  const add = useAddSoundByUrl();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');

  if (!open) {
    return (
      <Button
        variant="secondary"
        size="s"
        className="gap-2"
        data-djpad="addurl"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Icon name="link" size="sm" />
        По ссылке
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div data-djpad="addurl-panel" className="flex items-center gap-1">
        <Input
          data-djpad="addurl-input"
          placeholder="https://…/звук.mp3"
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
          }}
          className="w-52"
        />
        <Button
          variant="primary"
          size="s"
          data-djpad="addurl-submit"
          loading={add.isPending}
          disabled={url.trim() === ''}
          aria-describedby={url.trim() === '' ? 'djpad-addurl-why' : undefined}
          onClick={() => {
            add.mutate(
              { url: url.trim(), name: 'Звук по ссылке' },
              {
                onSuccess: () => {
                  toast.show({
                    tone: 'success',
                    title: 'Звук добавлен',
                    text: 'Файл скачан к нам: в эфире оверлей не ходит на чужие адреса.',
                  });
                  setUrl('');
                  setOpen(false);
                },
              },
            );
          }}
        >
          Добавить
        </Button>
        <Button
          variant="ghost"
          size="s"
          data-djpad="addurl-cancel"
          onClick={() => {
            setOpen(false);
          }}
        >
          Отмена
        </Button>
      </div>
      {url.trim() === '' && <Reason id="djpad-addurl-why">Вставьте ссылку на файл звука.</Reason>}
    </div>
  );
}

/** Строка звука: порядок, прослушивание, имя, триггер, цвет, удаление. */
function SoundRowView({
  sound,
  index,
  total,
  onDrop,
}: {
  sound: SoundRow;
  index: number;
  total: number;
  onDrop: (from: number, to: number) => void;
}) {
  const patch = usePatchSound();
  const remove = useRemoveSound();
  const reorder = useReorderSounds();
  const sounds = useSounds();
  const gifts = useGifts();
  const toast = useToast();
  const audio = useRef<HTMLAudioElement | null>(null);

  const [draft, setDraft] = useState(sound.trigger);
  const ids = (sounds.data?.sounds ?? []).map((item) => item.id);

  const move = (to: number) => {
    const next = [...ids];
    const [moved] = next.splice(index, 1);

    if (moved !== undefined) {
      next.splice(to, 0, moved);
      reorder.mutate(next);
    }
  };

  /* Кнопка сохранения отключается С ОБЪЯСНЕНИЕМ, почему: отключённая кнопка
     без причины - это тупик, из которого человек выходит перезагрузкой. */
  const problem =
    draft.type === 'keyword' && (draft.keyword ?? '').trim() === ''
      ? 'Впишите слово, по которому сработает звук'
      : null;

  return (
    <li
      data-djpad-row={sound.id}
      data-control-row="djpad-row"
      /* Перетаскивание - привычный способ менять порядок; кнопки «выше» и
         «ниже» рядом, потому что мышью умеют не все и не всегда. */
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', String(index));
        event.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        event.preventDefault();
        const from = Number(event.dataTransfer.getData('text/plain'));

        if (Number.isFinite(from) && from !== index) {
          onDrop(from, index);
        }
      }}
      className="border-foreground/10 rounded-lg @container flex flex-col gap-2 border p-2"
    >
      <div className="flex items-center gap-2">
        {/* Порядок = номера падов. Перетаскивание мышью и кнопками: кнопки
            нужны клавиатуре, перетаскивание - привычке. */}
        <span
          data-djpad-row-grip={sound.id}
          className="text-foreground/50 flex shrink-0 flex-col"
          aria-hidden="true"
        >
          <Icon name="drag" size="sm" />
        </span>

        <div className="flex shrink-0 flex-col">
          <Button
            variant="ghost"
            size="s"
            className="px-1"
            data-djpad-row-up={sound.id}
            aria-label="Выше"
            disabled={index === 0}
            onClick={() => {
              move(index - 1);
            }}
          >
            <Icon name="chevronUp" size="sm" />
          </Button>
          <Button
            variant="ghost"
            size="s"
            className="px-1"
            data-djpad-row-down={sound.id}
            aria-label="Ниже"
            disabled={index === total - 1}
            onClick={() => {
              move(index + 1);
            }}
          >
            <Icon name="chevronDown" size="sm" />
          </Button>
        </div>

        {/* Номер пада = прослушивание. ЛОКАЛЬНОЕ, и так и подписано. */}
        <Button
          variant="ghost"
          size="s"
          className="shrink-0"
          data-djpad-row-pad={sound.id}
          title="Прослушать здесь — в эфир не уйдёт"
          style={{ borderColor: sound.color }}
          onClick={() => {
            audio.current?.pause();
            audio.current = new Audio(sound.url);
            void audio.current.play().catch(() => {
              toast.show({
                tone: 'warning',
                title: 'Не проигралось',
                text: 'Браузер не дал воспроизвести звук. Нажмите ещё раз.',
              });
            });
          }}
        >
          {index + 1}
        </Button>

        <Input
          data-djpad-row-name={sound.id}
          aria-label="Название звука"
          value={sound.name}
          onChange={(event) => {
            patch.mutate({ id: sound.id, name: event.target.value });
          }}
          className="min-w-0 flex-1"
        />

        <label className="text-foreground flex shrink-0 items-center gap-1 text-xs @max-[520px]:hidden">
          <Switch
            data-djpad-row-enable={sound.id}
            checked={sound.enabled}
            onCheckedChange={(next) => {
              patch.mutate({ id: sound.id, enabled: next === true });
            }}
          />
          Включён
        </label>

        <Destructive
          id="sound-delete"
          className="shrink-0"
          data={{ 'data-djpad-row-delete': sound.id }}
          ariaLabel={`Удалить «${sound.name}»`}
          icon={<Icon name="delete" size="sm" />}
          title={`Удалить «${sound.name}»?`}
          confirm="Удалить"
          onConfirm={() => {
            remove.mutate(sound.id, {
              onSuccess: () => {
                toast.show({
                  tone: 'success',
                  title: 'Звук удалён',
                  text: 'Вместе с ним убраны его пад и привязки во всех играх.',
                });
              },
            });
          }}
        >
          Вместе со звуком уйдут его пад и привязки во всех играх. Вернуть их можно только заново.
        </Destructive>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-foreground flex flex-col gap-1 text-xs">
          Когда играть
          <Csel
            value={draft.type}
            aria-label="Когда играть"
            triggerClassName="w-44"
            options={TRIGGERS}
            onChange={(next) => {
              setDraft({ type: next });
            }}
          />
        </label>

        {/* Условные поля: у подписки ключевого слова нет, и его тут не рисуют. */}
        {draft.type === 'keyword' && (
          <label className="text-foreground flex flex-col gap-1 text-xs">
            Слово в комментарии
            <Input
              data-djpad-row-keyword={sound.id}
              value={draft.keyword ?? ''}
              onChange={(event) => {
                setDraft({ ...draft, keyword: event.target.value });
              }}
              className="w-44"
            />
          </label>
        )}

        {draft.type === 'gift' && (
          <>
            <label className="text-foreground flex flex-col gap-1 text-xs">
              Подарок
              <Csel
                value={draft.giftId ?? ''}
                aria-label="Подарок"
                triggerClassName="w-48"
                options={[
                  { value: '', label: 'Любой подарок' },
                  ...(gifts.data?.gifts ?? [])
                    .slice(0, 200)
                    .map((gift) => ({ value: gift.id, label: gift.name })),
                ]}
                onChange={(next) => {
                  setDraft({ ...draft, giftId: next === '' ? undefined : next });
                }}
              />
            </label>

            {draft.giftId === undefined && (
              <label className="text-foreground flex flex-col gap-1 text-xs">
                От скольких монет
                <Input
                  type="number"
                  min={1}
                  data-djpad-row-mincoins={sound.id}
                  value={draft.minCoins ?? 1}
                  onChange={(event) => {
                    setDraft({ ...draft, minCoins: Number(event.target.value) || 1 });
                  }}
                  className="w-28"
                />
              </label>
            )}
          </>
        )}

        <div className="text-foreground flex flex-col gap-1 text-xs">
          Цвет
          <div role="group" aria-label="Цвет пада" className="flex gap-1">
            {COLORS.map((color) => (
              <Button
                variant="ghost"
                key={color}
                data-djpad-row-color={color}
                aria-label={`Цвет ${color}`}
                aria-pressed={sound.color.toLowerCase() === color}
                /* Выбранный цвет помечен кольцом С ЗАЗОРОМ: кольцо в тон
                   подложке на тёмной теме неотличимо от невыбранного. */
                className={`size-6 rounded-full ${
                  sound.color.toLowerCase() === color
                    ? 'ring-foreground ring-2 ring-offset-2 ring-offset-transparent'
                    : 'ring-foreground/20 ring-1'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  patch.mutate({ id: sound.id, color });
                }}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Button
            variant="primary"
            size="s"
            data-djpad-row-save={sound.id}
            disabled={problem !== null}
            aria-describedby={problem === null ? undefined : `djpad-row-why-${sound.id}`}
            onClick={() => {
              patch.mutate(
                { id: sound.id, trigger: draft },
                {
                  onSuccess: () => {
                    toast.show({ tone: 'success', title: 'Условие сохранено' });
                  },
                },
              );
            }}
          >
            Сохранить
          </Button>
          {problem !== null && (
            <Reason id={`djpad-row-why-${sound.id}`} data-djpad-row-problem={sound.id}>
              {problem}
            </Reason>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Обозреватель звуков: наш набор.
 *
 * ЧИСЛО НАСТОЯЩЕЕ. Эталон показывает «1 665» из библиотеки вендора; у нас
 * свой набор, и сколько в нём позиций, столько и написано.
 */
function SoundBrowser({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState('');
  /**
   * Плитки подгружаются ПО ДВАДЦАТЬ при прокрутке вниз - как у эталона.
   *
   * Не ради красоты: сто шестьдесят плиток сразу - это сто шестьдесят
   * элементов в дереве окна, которое и так лежит поверх живой консоли.
   */
  const [limit, setLimit] = useState(20);
  const library = useSoundLibrary(query);
  const add = useAddLibrarySound();
  const toast = useToast();
  const audio = useRef<HTMLAudioElement | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  const data = library.data;
  const shown = (data?.items ?? []).slice(0, limit);
  const more = (data?.items ?? []).length > shown.length;

  useEffect(() => {
    const node = sentinel.current;

    if (node === null || !more) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setLimit((current) => current + 20);
      }
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [more, shown.length]);

  return (
    <div data-browser="root" className="flex flex-col gap-3">
      {/* Крошка НАЗЫВАЕТ, куда ведёт: «Назад» без адреса заставляет
          вспоминать, откуда пришёл. */}
      <Button
        variant="ghost"
        size="s"
        className="self-start gap-1 ps-0"
        data-browser="back"
        onClick={onBack}
      >
        <Icon name="chevronLeft" size="sm" />
        Звуки и пады
      </Button>

      <div>
        <h2 className="text-foreground text-xl font-semibold">Выбор звука</h2>
        <p className="text-foreground/80 text-sm">
          Нажмите, чтобы прослушать здесь, затем добавьте в пад. В эфир при прослушивании ничего не
          уходит.
        </p>
      </div>

      <div data-control-row="browser-head" className="@container flex items-center gap-2">
        {/* ПОЛЕ СО ЗНАЧКОМ - `InputGroup`, как поиск каталога. Прежняя
            редакция оборачивала поле в `label.input` прежней библиотеки:
            получалась рамка в рамке. */}
        <InputGroup className="min-w-0 flex-1">
          <InputGroupAddon>
            <Icon name="search" size="sm" />
          </InputGroupAddon>
          <InputGroupInput
            data-browser="search"
            aria-label="Найти звук"
            placeholder="Найти звук"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setLimit(20);
            }}
          />
        </InputGroup>

        <span data-browser="count" className="text-foreground/80 shrink-0 text-sm tabular-nums">
          {data?.total ?? 0} звуков
        </span>
      </div>

      {data !== undefined && data.items.length === 0 ? (
        <p data-browser="empty" className="text-foreground/80 text-sm">
          По запросу ничего нет. Попробуйте короче — «удар», «сигнал», «победа».
        </p>
      ) : (
        /* ДВЕ КОЛОНКИ, А НЕ ТРИ - ПО ЗАМЕРУ, А НЕ ПО СХОДСТВУ СЕТКИ.
           При трёх колонках на имя оставалось 107 px, а самое длинное имя
           каталога требует 141: обрезано было восемь названий из сорока
           видимых (находка владельца). Названия у нас свои и длиннее
           эталонных - значит и сетка своя. */
        <div data-browser="grid" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {shown.map((item) => (
            <div
              key={item.id}
              data-browser-tile={item.id}
              className="border-foreground/10 rounded-lg flex items-center gap-2 border p-2"
            >
              <Button
                variant="ghost"
                size="s"
                className="shrink-0 px-1"
                data-browser-play={item.id}
                aria-label={`Прослушать «${item.name.ru}»`}
                title="Прослушать здесь — в эфир не уйдёт"
                onClick={() => {
                  audio.current?.pause();
                  audio.current = new Audio(item.url);
                  void audio.current.play().catch(() => undefined);
                }}
              >
                <Icon name="play" size="sm" />
              </Button>

              <span
                data-browser-name
                className="text-foreground min-w-0 flex-1 truncate text-sm font-semibold"
              >
                {item.name.ru}
              </span>

              {/* Действие нейтральное: акцент в разделе принадлежит одному
                  действию, а не сорока восьми одинаковым кнопкам. */}
              <Button
                variant="secondary"
                size="s"
                className="shrink-0"
                data-browser-add={item.id}
                onClick={() => {
                  add.mutate(item.id, {
                    onSuccess: () => {
                      toast.show({ tone: 'success', title: `«${item.name.ru}» — в вашем списке` });
                    },
                  });
                }}
              >
                Добавить
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Метка подгрузки: доехали до неё - приехали ещё двадцать плиток. */}
      {more && <div ref={sentinel} data-browser="more" className="h-4" />}
    </div>
  );
}
